// Built-ins
import React, { useEffect, useState } from "react";
import axios from "axios";

// Custom
import NavigationBar from "../navigation_bar/navigation_bar";
import Footer from "../footer/footer";
import { pageHeader, headers, loader, formatDisplayDateTime, formatDisplayDate } from "../../global_vars";

const API_BASE = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
  ? 'https://ultima.icsgr.com'
  : '';

const LIST_URL = API_BASE + "/api/view/notifications/?all=1";
const MARK_READ_URL = API_BASE + "/api/view/notifications/mark_read/";
const MARK_ALL_URL = API_BASE + "/api/view/notifications/mark_all_read/";
const UNBLOCK_URL = API_BASE + "/api/view/notifications/unblock_user/";
const DELETE_URL = API_BASE + "/api/view/notifications/delete/";

const CATEGORIES = [
  {
    key: 'tasks',
    title: 'Tasks',
    description: 'Assignments, updates, comments, and completions',
    accent: '#4a7c9b',
  },
  {
    key: 'documents',
    title: 'Documents & Deadlines',
    description: 'Expiring documents and upcoming deadlines',
    accent: '#b8860b',
  },
  {
    key: 'security',
    title: 'Security',
    description: 'Login lockouts and account security alerts',
    accent: '#c0392b',
  },
];

function formatDetailValue(label, value) {
  if (!value) return value;
  if (
    label.toLowerCase().includes('deadline') ||
    label === 'Valid until' ||
    label === 'Issue date' ||
    label === 'Expires'
  ) {
    return formatDisplayDate(value);
  }
  return value;
}

function NotificationCard({ notification, onMarkRead, onRemove, onUnblock, canDelete }) {
  const n = notification;
  const unreadStyle = n.read ? '#fff' : '#f7fbff';

  return (
    <div
      className="card"
      style={{
        marginBottom: 12,
        borderColor: n.read ? '#eee' : '#cfe2f3',
        background: unreadStyle,
        boxShadow: n.read ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{n.title || n.type_label}</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{n.type_label}</div>
        </div>
        {!n.read ? (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#0d6efd', whiteSpace: 'nowrap' }}>Unread</span>
        ) : null}
      </div>
      <div className="card-body" style={{ padding: '12px 14px' }}>
        <div style={{ marginBottom: 10, lineHeight: 1.45 }}>{n.message}</div>
        {Array.isArray(n.details) && n.details.length > 0 ? (
          <div
            style={{
              background: '#f8f9fa',
              border: '1px solid #ececec',
              borderRadius: 6,
              padding: '10px 12px',
              fontSize: 13,
            }}
          >
            {n.details.map((row) => (
              <div key={`${row.label}-${row.value}`} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <span style={{ minWidth: 118, color: '#666', fontWeight: 600 }}>{row.label}</span>
                <span style={{ flex: 1 }}>{formatDetailValue(row.label, row.value)}</span>
              </div>
            ))}
          </div>
        ) : null}
        <div style={{ textAlign: 'right', marginTop: 10, fontSize: 12, color: '#666' }}>
          {formatDisplayDateTime(n.created_at)}
        </div>
      </div>
      <div className="card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {n.related_project ? (
            <a href={`/data_management/project/${n.related_project}`} className="btn btn-sm btn-link" style={{ padding: 0 }}>
              View project
            </a>
          ) : null}
          {n.related_task ? (
            <a href={`/data_management/project_task/${n.related_task}`} className="btn btn-sm btn-link" style={{ padding: 0 }}>
              View task
            </a>
          ) : null}
          {n.related_document ? (
            <a href={`/data_management/document/${n.related_document}`} className="btn btn-sm btn-link" style={{ padding: 0 }}>
              View document
            </a>
          ) : null}
          {n.related_client ? (
            <a href={`/data_management/client/${n.related_client}`} className="btn btn-sm btn-link" style={{ padding: 0 }}>
              View client
            </a>
          ) : null}
          {!n.read ? (
            <button onClick={() => onMarkRead(n.id)} className="btn btn-sm btn-outline-primary">Mark as read</button>
          ) : null}
          {n.type === 'security_lockout' && n.locked_username ? (
            <button
              onClick={() => onUnblock(n.locked_username)}
              className="btn btn-sm btn-outline-danger"
            >
              Unblock user
            </button>
          ) : null}
        </div>
        <div>
          {canDelete ? (
            <button onClick={() => onRemove(n.id)} className="btn btn-sm btn-outline-secondary">Delete</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  function groupByCategory(list) {
    const groups = { tasks: [], documents: [], security: [] };
    (list || []).forEach((n) => {
      const key = n.category || 'tasks';
      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    });
    return groups;
  }

  async function load() {
    try {
      const currentHeaders = { ...headers, Authorization: "Token " + localStorage.getItem("userToken") };
      const res = await axios.get(LIST_URL, { headers: currentHeaders });
      setItems(res?.data?.notifications || []);
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => { load(); }, []);

  async function markRead(id) {
    const currentHeaders = { ...headers, Authorization: "Token " + localStorage.getItem("userToken") };
    await axios.post(MARK_READ_URL + `?id=${id}`, null, { headers: currentHeaders });
    load();
  }

  async function markAll() {
    const currentHeaders = { ...headers, Authorization: "Token " + localStorage.getItem("userToken") };
    await axios.post(MARK_ALL_URL, null, { headers: currentHeaders });
    load();
  }

  function canDelete() {
    const role = localStorage.getItem('role');
    return role === 'A' || role === 'S';
  }

  async function remove(id) {
    const currentHeaders = { ...headers, Authorization: "Token " + localStorage.getItem("userToken") };
    await axios.post(DELETE_URL + `?id=${id}`, null, { headers: currentHeaders });
    load();
  }

  async function unblockUser(username) {
    if (!username) return;
    const currentHeaders = { ...headers, Authorization: "Token " + localStorage.getItem("userToken") };
    await axios.post(UNBLOCK_URL + `?username=${encodeURIComponent(username)}`, null, { headers: currentHeaders });
    load();
  }

  const groups = groupByCategory(items);
  const hasNotifications = CATEGORIES.some((cat) => (groups[cat.key] || []).length > 0);

  return (
    <>
      <NavigationBar />
      <div className="mainContainer">
        {pageHeader("notifications", "Notifications")}
        <div className="contentContainer">
          <div className="contentBody">
            {!loaded ? loader() : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  <div style={{ color: '#666', fontSize: 14 }}>
                    Notifications are grouped by type so you can quickly see what needs attention.
                  </div>
                  <button onClick={markAll} className="btn btn-sm btn-outline-secondary">Mark all as read</button>
                </div>

                {!hasNotifications ? (
                  <div className="card" style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                    No notifications
                  </div>
                ) : (
                  <div className="row">
                    {CATEGORIES.map((cat) => {
                      const columnItems = groups[cat.key] || [];
                      const unreadCount = columnItems.filter((n) => !n.read).length;
                      return (
                        <div key={cat.key} className="col-lg-4 col-md-12" style={{ marginBottom: 18 }}>
                          <div
                            style={{
                              borderTop: `4px solid ${cat.accent}`,
                              background: '#fafafa',
                              border: '1px solid #e8e8e8',
                              borderRadius: 8,
                              padding: '14px 14px 6px',
                              minHeight: 220,
                            }}
                          >
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontWeight: 800, fontSize: 17 }}>{cat.title}</div>
                                <span style={{ fontSize: 12, color: '#666' }}>
                                  {columnItems.length} total{unreadCount ? ` · ${unreadCount} unread` : ''}
                                </span>
                              </div>
                              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{cat.description}</div>
                            </div>

                            {columnItems.length === 0 ? (
                              <div style={{ color: '#888', fontSize: 13, padding: '12px 0' }}>No items in this category</div>
                            ) : (
                              columnItems.map((n) => (
                                <NotificationCard
                                  key={n.id}
                                  notification={n}
                                  onMarkRead={markRead}
                                  onRemove={remove}
                                  onUnblock={unblockUser}
                                  canDelete={canDelete()}
                                />
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default NotificationsPage;
